import { useState, type CSSProperties, type HTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { CheckCircleFilled, CloseCircleFilled, CloseOutlined, ExclamationCircleFilled, InfoCircleFilled } from "@ant-design/icons";
import { cx } from "./fieldContext";

export type AlertType = "info" | "success" | "warning" | "error";

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, "title" | "role"> {
  type?: AlertType;
  /** Kernaussage (Antd 6: `title`, früher `message`). */
  title?: ReactNode;
  message?: ReactNode;
  description?: ReactNode;
  showIcon?: boolean;
  icon?: ReactNode;
  action?: ReactNode;
  closable?: boolean | { closeIcon?: ReactNode; "aria-label"?: string };
  /** Antd-kompatibel: eigenes Schließen-Symbol. */
  closeIcon?: ReactNode;
  onClose?: (event: MouseEvent<HTMLButtonElement>) => void;
  afterClose?: () => void;
  /** Volle Breite ohne Rahmen, oben an einer Fläche. */
  banner?: boolean;
  className?: string;
  style?: CSSProperties;
  role?: string;
  id?: string;
}

const ICONS: Record<AlertType, ReactNode> = {
  info: <InfoCircleFilled />,
  success: <CheckCircleFilled />,
  warning: <ExclamationCircleFilled />,
  error: <CloseCircleFilled />,
};

/** Hinweisfläche des Kits (Ersatz für Antd `Alert`, gleiche Props). Fehler und Warnungen werden vorgelesen. */
export function Alert({ type, title, message, description, showIcon, icon, action, closable, closeIcon, onClose, afterClose, banner, className, style, role, id, ...rest }: AlertProps) {
  const [closed, setClosed] = useState(false);
  if (closed) return null;
  const tone: AlertType = type ?? (banner ? "warning" : "info");
  const headline = title ?? message;
  const withIcon = showIcon ?? Boolean(banner);
  return (
    <div
      id={id}
      className={cx("ck-alert", banner && "ck-alert--banner", description != null && "ck-alert--with-description", className)}
      data-type={tone}
      role={role ?? (tone === "error" || tone === "warning" ? "alert" : "status")}
      style={style}
      {...rest}
    >
      {withIcon ? <span className="ck-alert-icon" aria-hidden="true">{icon ?? ICONS[tone]}</span> : null}
      <div className="ck-alert-body">
        {headline != null ? <div className="ck-alert-message">{headline}</div> : null}
        {description != null ? <div className="ck-alert-description">{description}</div> : null}
        {action ? <div className="ck-alert-action">{action}</div> : null}
      </div>
      {closable ? (
        <button
          type="button"
          className="ck-alert-close"
          aria-label={typeof closable === "object" ? closable["aria-label"] : undefined}
          onClick={(event) => {
            onClose?.(event);
            setClosed(true);
            return afterClose?.();
          }}
        >
          {typeof closable === "object" && closable.closeIcon ? closable.closeIcon : closeIcon ?? <CloseOutlined />}
        </button>
      ) : null}
    </div>
  );
}
